import React, { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Select } from "@/pages/Projects";
import { useBranding } from "@/context/BrandingContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const blankFrom = (fields) =>
  Object.fromEntries(fields.map((f) => [f.key, f.default ?? (f.type === "number" ? "" : "")]));

/** Generic log panel: table + create dialog driven by a field spec. */
export const LogSection = ({
  title,
  slug,
  description,
  items,
  columns,
  fields,
  createPath,
  deletePath,
  reload,
  addLabel,
  renderActions,
}) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(() => blankFrom(fields));
  const { t } = useBranding();

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    const required = fields.find((f) => f.required && !String(form[f.key] || "").trim());
    if (required) return toast.error(`${required.label} is required`);
    const payload = {};
    fields.forEach((f) => {
      const v = form[f.key];
      if (v === "" || v === undefined) return;
      payload[f.key] = f.type === "number" ? Number(v) : v;
    });
    try {
      await api.post(createPath, payload);
      toast.success(`${title.replace(/s$/, "")} logged`);
      setOpen(false);
      setForm(blankFrom(fields));
      await reload();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${item.title}"?`)) return;
    try {
      await api.delete(`${deletePath}/${item.id}`);
      await reload();
      toast.success("Deleted");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  return (
    <section className="panel p-5" data-testid={`${slug}-section`}>
      <div className="mb-1 flex items-center justify-between gap-4">
        <h3 className="font-display text-base font-semibold text-slate-800">{t(title)}</h3>
        <Button size="sm" data-testid={`add-${slug}-btn`} onClick={() => { setForm(blankFrom(fields)); setOpen(true); }} className="gap-1.5" style={{ backgroundColor: "var(--brand-primary)" }}>
          <Plus size={14} /> {t(addLabel || `Log ${title.replace(/s$/, "")}`)}
        </Button>
      </div>
      {description && <p className="mb-3 text-xs text-slate-500">{t(description)}</p>}

      {items.length === 0 ? (
        <p className="py-6 text-center text-sm text-slate-400">Nothing logged yet.</p>
      ) : (
        <div className="-mx-5 overflow-x-auto px-5">
          <table className="w-full min-w-[720px] text-sm">
            <thead className="text-left text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                {columns.map((c) => <th key={c.key} className="pb-2 pr-3 font-semibold">{t(c.label)}</th>)}
                <th className="pb-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((it) => (
                <tr key={it.id} data-testid={`${slug}-row-${it.id}`}>
                  {columns.map((c) => (
                    <td key={c.key} className={`py-2.5 pr-3 ${c.key === "title" ? "font-medium text-slate-800" : "text-slate-600"}`}>
                      {c.render ? c.render(it) : it[c.key] || "—"}
                    </td>
                  ))}
                  <td className="py-2.5 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {renderActions?.(it)}
                      <button data-testid={`delete-${slug}-${it.id}`} onClick={() => remove(it)} aria-label={`Delete ${title}`} title="Delete" className="text-slate-400 transition-colors hover:text-red-600">
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid={`${slug}-dialog`} className="max-h-[88vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t(addLabel || `Log ${title.replace(/s$/, "")}`)}</DialogTitle>
            <DialogDescription>{t(description || `Add a new entry to the ${title.toLowerCase()} log.`)}</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {fields.map((f) => (
              <div key={f.key} className={f.span === 2 ? "sm:col-span-2" : ""}>
                <label className="field-label">{t(f.label)}{f.required ? " *" : ""}</label>
                {f.type === "select" ? (
                  <Select testId={`${slug}-${f.key}-select`} value={form[f.key]} onChange={set(f.key)} options={f.options || []} placeholder={f.placeholder || "Select"} />
                ) : f.type === "textarea" ? (
                  <Textarea data-testid={`${slug}-${f.key}-input`} rows={2} value={form[f.key] || ""} onChange={(e) => set(f.key)(e.target.value)} />
                ) : (
                  <Input
                    data-testid={`${slug}-${f.key}-input`}
                    type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"}
                    value={form[f.key] ?? ""}
                    placeholder={f.placeholder}
                    onChange={(e) => set(f.key)(e.target.value)}
                  />
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" data-testid={`${slug}-cancel-btn`} onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid={`${slug}-save-btn`} onClick={save} style={{ backgroundColor: "var(--brand-primary)" }}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export const StatusPill = ({ value }) => {
  const map = {
    Approved: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    Accepted: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    Realized: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    Delivered: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    Success: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    Rejected: "bg-red-50 text-red-700 ring-red-200",
    "Not Realized": "bg-red-50 text-red-700 ring-red-200",
    Critical: "bg-red-50 text-red-700 ring-red-200",
    Pending: "bg-amber-50 text-amber-800 ring-amber-200",
    Proposed: "bg-amber-50 text-amber-800 ring-amber-200",
    Improvable: "bg-amber-50 text-amber-800 ring-amber-200",
    "In Progress": "bg-sky-50 text-sky-700 ring-sky-200",
  };
  return (
    <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ${map[value] || "bg-slate-100 text-slate-600 ring-slate-200"}`}>
      {value || "—"}
    </span>
  );
};
